import { ReactNode, forwardRef } from 'react';

import classNames from 'classnames';

import { InfiniteScroll } from '../../InfiniteScroll';
import { CircularProgress } from '../../CircularProgress';
import { Box } from '../../Box';

import useStyles from './ListBox.styles';

/**
 * Свойства компонента {@link ListBox}
 */
interface ListBoxProps {
  /**
   * Указывает, есть ли ещё данные для загрузки.
   */
  hasMore: boolean;

  /**
   * Текущее количество данных.
   */
  dataLength: number;

  /**
   * Функция для загрузки следующего набора данных.
   */
  next: () => void;

  /**
   * Указывает, загружаются ли данные в данный момент.
   */
  loading: boolean;

  /**
   * Если true, применяются дополнительные стили для более длинных списков.
   */
  long?: boolean;

  /**
   * Дочерние элементы, которые будут отображены внутри списка.
   */
  children?: ReactNode | ReactNode[];
}

/**
 * ListBox — компонент, который предоставляет прокручиваемый список с возможностями бесконечной прокрутки.
 * Может отображать индикатор загрузки, когда загружаются новые данные.
 *
 * @internal
 * @param hasMore - Указывает, есть ли ещё данные для загрузки.
 * @param dataLength - Текущее количество данных.
 * @param next - Функция для загрузки следующего набора данных.
 * @param loading - Указывает, загружаются ли данные в данный момент.
 * @param long - Если true, применяются дополнительные стили для более длинных списков.
 * @param children - Дочерние элементы, которые будут отображены внутри списка.
 * @param ref - Ссылка, которая будет прикреплена к корневому элементу div.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ListBoxProps}
 */
export const ListBox = forwardRef<HTMLDivElement, ListBoxProps>(
  (
    { hasMore, dataLength, next, loading, long = false, children, ...rest },
    ref,
  ) => {
    const { classes } = useStyles();

    return (
      <div
        {...rest}
        id="address-input-scrollable"
        ref={ref}
        className={classNames(classes.scrollableWrapper, {
          [classes.longScrollableWrapper]: long,
        })}
      >
        <InfiniteScroll
          next={next}
          hasMore={hasMore}
          dataLength={dataLength}
          loader={
            loading && (
              <Box display="flex" justifyContent="center" p="16">
                <CircularProgress size={30} />
              </Box>
            )
          }
          endMessage={<div />}
          scrollId="address-input-scrollable"
        >
          <ul className={classes.listBox}>{children}</ul>
        </InfiniteScroll>
      </div>
    );
  },
);
